import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { isValidWebsiteUrl } from "../utils/urlValidation";
import { ScoreRing, scoreLabel } from "../components/SeoScoreCard";
import { MiniAuditPreview, AuditReportMockup } from "../components/ProductShowcase";
import "./Landing.css";

const FEATURES = [
  {
    icon: "🔍",
    title: "127 SEO Checks",
    desc: "Comprehensive analysis of meta tags, headings, canonicals, robots.txt, and indexability with a prioritized fix list.",
    link: "/audit",
    cta: "Start Auditing",
  },
  {
    icon: "⚡",
    title: "Lighthouse Integration",
    desc: "Measure PageSpeed performance, Core Web Vitals, and get technical optimization steps to cut load time.",
    link: "/pagespeed",
    cta: "Check PageSpeed",
  },
  {
    icon: "📈",
    title: "Core Web Vitals",
    desc: "Real-time metrics for LCP, FID, CLS scores with step-by-step developer guidelines.",
    link: "/audit",
    cta: "Check Vitals",
  },
  {
    icon: "🔗",
    title: "Broken Links Finder",
    desc: "Find broken outgoing links and loops before they hurt crawl budgets or page authority.",
    link: "/broken-links",
    cta: "Check Links",
  },
  {
    icon: "✨",
    title: "Schema Validation",
    desc: "Validate structured data JSON-LD and microformats to optimize for rich snippet search results.",
    link: "/deep-audit",
    cta: "Unlock Schema Checks",
    premium: true,
  },
  {
    icon: "🔠",
    title: "Keyword Optimization",
    desc: "Analyze density, volume, ranking keywords, and distribution to capture maximum search intent.",
    link: "/keywords",
    cta: "Research Keywords",
  },
];

const STEPS = [
  { n: "1", title: "Enter Your URL", body: "Paste any page URL to begin — no setup required." },
  { n: "2", title: "Run Analysis", body: "Our engine scans for SEO issues across 50+ checks." },
  { n: "3", title: "Get Insights", body: "Receive a prioritised report with clear recommendations." },
  { n: "4", title: "Optimize", body: "Fix issues, re-run, and watch your rankings climb." },
];

const STATS = [
  { value: "50+", label: "SEO checks per audit" },
  { value: "< 30s", label: "Average scan time" },
  { value: "Instant", label: "Actionable reports" },
];

const Landing = () => {
  const { isAuthenticated } = useAuth();
  const { showError } = useToast();
  const navigate = useNavigate();

  // Hero inline URL input (unauthenticated hook model)
  const [heroUrl, setHeroUrl] = useState("");

  // Sticky CTA: appears once hero scrolls out of view
  const [stickyVisible, setStickyVisible] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const observer = new IntersectionObserver(
      ([entry]) => setStickyVisible(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  const handleHeroAudit = (e) => {
    e.preventDefault();
    const trimmed = heroUrl.trim();
    if (!trimmed) return;
    // Prepend https:// if the user omitted the scheme
    const url = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    
    if (!isValidWebsiteUrl(url)) {
      showError("Please enter a valid website URL.");
      return;
    }
    
    navigate(`/audit?url=${encodeURIComponent(url)}`);
  };

  return (
    <div className="landing">
      <Helmet>
        <title>Nova SEO — Website Audit, PageSpeed & Keyword Analysis</title>
        <meta name="description" content="Stop guessing and start ranking with our SEO platform. Get comprehensive audits, PageSpeed analysis, and keyword research tools in one place." />
        <link rel="canonical" href={window.location.origin} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.origin} />
        <meta property="og:title" content="Nova SEO — Stop Guessing. Start Ranking." />
        <meta property="og:description" content="Comprehensive SEO audit, PageSpeed analysis, and keyword research tools to help your website rank higher." />
        <meta property="og:image" content={`${window.location.origin}/og-image.png`} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={window.location.origin} />
        <meta name="twitter:title" content="Nova SEO — Stop Guessing. Start Ranking." />
        <meta name="twitter:description" content="Comprehensive SEO audit, PageSpeed analysis, and keyword research tools to help your website rank higher." />
        <meta name="twitter:image" content={`${window.location.origin}/og-image.png`} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Nova SEO",
            "operatingSystem": "All",
            "applicationCategory": "SEO Tool",
            "description": "Comprehensive SEO audit, PageSpeed analysis, and keyword research tools.",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          })}
        </script>
      </Helmet>

      {/* ── Sticky CTA bar — slides in once hero leaves viewport ── */}
      <div
        className={`sticky-cta-bar${stickyVisible ? " sticky-cta-bar--visible" : ""}`}
        aria-hidden={!stickyVisible}
      >
        <span className="sticky-cta-label">✓ 500+ website audits run during beta</span>
        <Link to="/audit" className="sticky-cta-btn">
          {isAuthenticated ? "Run an Audit" : "Audit — No Sign-up"}
        </Link>
      </div>

      {/* ── Hero ── */}
      <section className="hero" ref={heroRef}>
        <div className="hero-content">
          {/* <div className="hero-eyebrow">Free SEO platform</div> */}
          <h1 className="hero-title">
            Stop Guessing.<br />
            <span className="hero-title-accent">Start Ranking.</span>
          </h1>
          <p className="hero-subtitle">
            Audit, analyse, and optimise your website from one place.
            Actionable insights on performance, keywords, and broken links
            — no credit card, no fluff.
          </p>

          <div className="hero-ctas">
            <form className="hero-url-form" onSubmit={handleHeroAudit} role="search" aria-label="Start an audit">
              <input
                className="hero-url-input"
                type="text"
                placeholder="your-website.com"
                value={heroUrl}
                onChange={(e) => setHeroUrl(e.target.value)}
                aria-label="Your website URL"
                autoComplete="url"
                spellCheck={false}
              />
              <button type="submit" className="cta-primary" disabled={!heroUrl.trim()}>
                Audit Now
              </button>
            </form>
            {/* 
            <div className="sample-report-link-container">
              <Link to="/audit?url=https%3A%2F%2Fgithub.com" className="sample-report-link">
                View Sample SEO Report →
              </Link>
            </div>
            */}
          </div>

          {/* Trust Indicators */}
          <div className="hero-trust-indicators">
            <div className="trust-stars">
              <span className="stars">★★★★★</span>
              <span className="stars-lbl">4.9/5 Agency Rating</span>
            </div>
            <div className="trust-divider">|</div>
            <div className="trust-badge-item">✓ No Credit Card</div>
            <div className="trust-divider">|</div>
            <div className="trust-badge-item">✓ 30s Real-Time Scan</div>
          </div>

          {isAuthenticated && (
            <div style={{ marginTop: '0.5rem' }}>
              <Link to="/dashboard" className="cta-secondary" style={{ width: '100%', maxWidth: '460px' }}>Go to Dashboard</Link>
            </div>
          )}
        </div>

        <div className="hero-showcase">
          <MiniAuditPreview />
        </div>
      </section>

      {/* ── Actual Audit Report Screenshot (mockup) with callouts ── */}
      <AuditReportMockup />

      {/* ── Features ── */}
      <section className="features" aria-labelledby="features-heading">
        <div className="section-header">
          <h2 className="section-title" id="features-heading">Powerful SEO Tools for Better Rankings</h2>
          <p className="section-subtitle">
            Six integrated tools, one dashboard — from quick wins to deep analysis.
          </p>
        </div>

        <div className="features-grid">
          {FEATURES.map((f) => (
            <article
              key={f.title}
              className={`feature-card${f.premium ? " premium" : ""}`}
            >
              {f.premium && <div className="premium-badge">Premium</div>}
              <div className="feature-header">
                <span className="feature-icon" aria-hidden="true">{f.icon}</span>
                <h3 className="feature-title">{f.title}</h3>
              </div>
              <p className="feature-desc">{f.desc}</p>
              <Link to={f.link} className="feature-link" aria-label={`${f.cta} — ${f.title}`}>
                {f.cta} →
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* ── Optimization Workflow ── */}
      <section className="optimization-workflow" aria-labelledby="workflow-heading">
        <div className="section-header">
          <h2 className="section-title" id="workflow-heading">The Optimization Workflow</h2>
          <p className="section-subtitle">A simple, continuous loop to audit, optimize, and rank.</p>
        </div>
        <div className="workflow-grid">
          <div className="workflow-card">
            <div className="workflow-step-num">01</div>
            <h3>Run Audit</h3>
            <p>Enter any URL to crawl code, server configurations, and performance metrics instantly.</p>
            <div className="workflow-visual visual-audit">
              <span className="visual-url">example.com</span>
              <span className="visual-pulse">Scanning...</span>
            </div>
          </div>
          <div className="workflow-card">
            <div className="workflow-step-num">02</div>
            <h3>Find Issues</h3>
            <p>See exactly what is holding your rankings back, prioritized by severity and impact.</p>
            <div className="workflow-visual visual-issues">
              <div className="visual-issue error">❌ Missing canonical URL</div>
              <div className="visual-issue warning">⚠️ Image alt text missing (8)</div>
            </div>
          </div>
          <div className="workflow-card">
            <div className="workflow-step-num">03</div>
            <h3>Fix Problems</h3>
            <p>Get developer-friendly recommendations and exact code modifications for immediate fixes.</p>
            <div className="workflow-visual visual-fixes">
              <pre className="visual-code"><code>{`<link rel="canonical"\n  href="https://site.com" />`}</code></pre>
            </div>
          </div>
          <div className="workflow-card">
            <div className="workflow-step-num">04</div>
            <h3>Improve Rankings</h3>
            <p>Re-scan to verify your optimizations, see your scores jump, and watch rankings scale.</p>
            <div className="workflow-visual visual-scores">
              <div className="visual-score-ring">98%</div>
              <div className="visual-score-lbl">SEO Score</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="cta-section" aria-labelledby="cta-heading">
        <h2 id="cta-heading">Boost Your Search Visibility Today</h2>
        <p>Run your first audit in under 30 seconds.</p>
        <Link to="/audit" className="cta-large">
          {isAuthenticated ? "Start Auditing Now" : "Get Started Now"}
        </Link>
      </section>

    </div>
  );
};

export default Landing;